import { faCalendarDays } from '@fortawesome/free-regular-svg-icons'
import { parseISO } from 'date-fns'
import type { FC } from 'react'
import { useEffect, useCallback } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import styled from 'styled-components'

import AccountantReferenceCard from '@/components/AccountantReferenceCard'
import RefereeActionButtons from '@/components/ActionButtons/RefereeActionButtons'
import DetailPill from '@/components/DetailPill'
import Details from '@/components/Details'
import DetailsCard from '@/components/DetailsCard'
import ExpectedError from '@/components/ExpectedError'
import { Header } from '@/components/Header'
import PageContainer from '@/components/PageContainer'
import useAccountantReferee from '@/hooks/useAccountantReferee'
import useUser from '@/hooks/useUser'
import { useSignalREventHandler } from '@/lib/signalR'
import type {
  EventHandler,
  ShouldHandleEvent,
} from '@/lib/signalR/hooks/useSignalREventHandler'
import { EVENT_TYPES } from '@/lib/signalR/hooks/useSignalREventHandler'
import { formatDate } from '@/utils/date'
import { isAccountant } from '@/utils/subjectHelpers'
import { Layout, Container, ColumnContainer, PanelContainer } from '@/views/Applicant'

interface Params {
  accountantId: string
}

const DetailPillsContainer = styled.div`
  display: grid;
  grid-gap: 10px;
  grid-template-columns: 170px;
`

const AccountantRefereePage: FC = () => {
  const history = useHistory()

  const { accountantId } = useParams<Params>()

  const { customerId } = useUser()

  const { loading, error, application, fetchSingleApplication, accountantReferee } =
    useAccountantReferee()

  const onClose = () => history.goBack()

  useEffect(() => {
    if (customerId) {
      fetchSingleApplication(customerId, accountantId)
    }
  }, [fetchSingleApplication, accountantId, customerId])

  const shouldHandleSignalREvent = useCallback<ShouldHandleEvent>(
    event => application?.subjectId === event.SubjectId,
    [application],
  )

  const handleSignalREvent = useCallback<EventHandler>(
    event => {
      switch (event.EventType) {
        case EVENT_TYPES.ReportGenerated:
        case EVENT_TYPES.SubjectOutcomeOverridden:
          fetchSingleApplication(event.CustomerId, event.SubjectId)
          break
      }
    },
    [fetchSingleApplication],
  )

  useSignalREventHandler({
    shouldHandleEvent: shouldHandleSignalREvent,
    eventHandler: handleSignalREvent,
  })

  const breadcrumbs = [
    {
      label: 'Referees',
    },
    {
      label: 'Accountant',
    },
  ]

  const tenancyStartDate =
    application?.assets && application.assets[0].attributes.startDate

  if (error !== undefined) {
    return (
      <ExpectedError
        text={error}
        imagePath="/assets/svg/failed-to-retrieve-applicant.svg"
      />
    )
  }

  return (
    <>
      <Header
        isLoading={loading}
        hasError={error !== undefined}
        name={application?.name?.toLowerCase() ?? ''}
        subjectId={application?.subjectId}
        breadcrumbs={breadcrumbs}
      />
      <PageContainer>
        {loading
          ? 'Loading...'
          : error
            ? error
            : application &&
              (!isAccountant(application.type) ? (
                <>The subject is not an accountant referee</>
              ) : (
                <Layout>
                  <Details
                    application={application}
                    actionButtons={
                      accountantReferee !== undefined && (
                        <RefereeActionButtons
                          refereeType="accountant"
                          referee={accountantReferee}
                        />
                      )
                    }
                    detailPills={
                      <DetailPillsContainer>
                        {tenancyStartDate && (
                          <DetailPill
                            icon={faCalendarDays}
                            title="Tenancy start date:"
                            detail={formatDate(parseISO(tenancyStartDate))}
                          />
                        )}
                      </DetailPillsContainer>
                    }
                  />
                  <Container>
                    <ColumnContainer>
                      <PanelContainer>
                        <AccountantReferenceCard application={application} />
                      </PanelContainer>
                    </ColumnContainer>
                    <ColumnContainer>
                      <PanelContainer>
                        <DetailsCard
                          type="Accountant"
                          applicationId={application.applicationId}
                          subjectId={application.subjectId}
                          agencyName={application.customerClientName}
                          onClose={onClose}
                          application={application}
                          fetchSingleApplication={fetchSingleApplication}
                          alwaysExpand
                        />
                      </PanelContainer>
                    </ColumnContainer>
                  </Container>
                </Layout>
              ))}
      </PageContainer>
    </>
  )
}

export default AccountantRefereePage
